import React, { useState, useEffect } from 'react';

// Brand Colors
const colors = {
    primary: '#f79421',    // Orange
    secondary: '#262262',  // Deep Purple
    blue: '#477CBF',       // Blue
    white: '#fff'          // White
};

const Chatbot = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Event handlers for text label
    const handleTextMouseEnter = (e) => {
        e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
        e.currentTarget.style.border = `1px solid ${colors.blue}`;
    };

    const handleTextMouseLeave = (e) => {
        e.currentTarget.style.transform = 'translateY(0px) scale(1)';
        e.currentTarget.style.background = `linear-gradient(135deg, ${colors.white} 0%, #f8faff 50%, ${colors.white} 100%)`;
        e.currentTarget.style.border = `2px solid ${colors.primary}`;
    };

    // Event handlers for chatbot image
    const handleImageMouseEnter = (e) => {
        e.currentTarget.style.transform = 'scale(1.05)';
        e.currentTarget.style.background = `linear-gradient(135deg, rgb(180, 230, 255) 0%, #e0f0ff 30%, rgb(120, 180, 255) 70%, #ffcc80 100%)`;
    };

    const handleImageMouseLeave = (e) => {
        e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
        e.currentTarget.style.background = `linear-gradient(135deg, rgb(208, 243, 255) 0%, #f0f8ff 30%, rgb(157, 211, 255) 70%, #ffeaa7 100%)`;
    };

    return (
        <div style={containerStyle(isMobile)}>
            {/* Text Label */}
            <div style={textContainerStyle}>
                <a href="https://www.facebook.com/groups/tuvantuyensinhdhcngtvt" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                    <div
                        style={textLabelStyle}
                        onMouseEnter={handleTextMouseEnter}
                        onMouseLeave={handleTextMouseLeave}
                    >
                        <span style={textSpanStyle}>
                            Tham gia nhóm tư vấn
                        </span>

                        {/* Enhanced shine effect */}
                        <div style={shineEffectStyle} />

                        {/* Arrow pointing to chatbot */}
                        <div style={arrowStyle} />
                    </div>
                </a>

                {/* Chatbot Image */}
                <a href="https://www.facebook.com/groups/tuvantuyensinhdhcngtvt" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                    <div
                        style={chatbotImageStyle(isMobile)}
                        onMouseEnter={handleImageMouseEnter}
                        onMouseLeave={handleImageMouseLeave}
                    >
                        <img src="/chatbot-removebg-preview.png" alt="UTT Chatbot" style={imageStyle} />

                        {/* Pulse animation ring */}
                        <div style={pulseRingStyle} />
                    </div>
                </a>
            </div>

            {/* CSS Animations */}
            <style jsx>{`
                @keyframes pulse {
                    0% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.15); opacity: 0.7; }
                    100% { transform: scale(1); opacity: 1; }
                }

                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-8px); }
                }

                @keyframes shine {
                    0% { left: -200%; }
                    100% { left: 200%; }
                }

                @keyframes gradientShift {
                    0%, 100% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                }

                @keyframes textGlow {
                    0% { filter: drop-shadow(0 0 2px rgba(247, 148, 33, 0.5)); }
                    100% { filter: drop-shadow(0 0 8px rgba(71, 124, 191, 0.8)) drop-shadow(0 0 12px rgba(38, 34, 98, 0.6)); }
                }
            `}</style>
        </div>
    );
};

// Styles
const containerStyle = (isMobile) => ({
    position: 'fixed',
    bottom: isMobile ? '20px' : '30px',
    right: isMobile ? '20px' : '30px',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '10px',
    animation: 'float 4s ease-in-out infinite'
});

const textContainerStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
};

const linkStyle = {
    textDecoration: 'none',
    display: 'block'
};

const textLabelStyle = {
    background: `linear-gradient(135deg, ${colors.white} 0%, #f8faff 50%, ${colors.white} 100%)`,
    padding: '10px 16px',
    borderRadius: '25px',
    boxShadow: '0 8px 15px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
    whiteSpace: 'nowrap',
    fontSize: '14px',
    fontWeight: '100',
    color: colors.secondary,
    border: `2px solid ${colors.primary}`,
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    cursor: 'pointer'
};

const textSpanStyle = {
    background: `linear-gradient(135deg, ${colors.primary} 0%, #ff6b35 25%, ${colors.blue} 50%, ${colors.secondary} 75%, #e74c3c 100%)`,
    backgroundSize: '200% 200%',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    fontWeight: '650',
    letterSpacing: '1px',
    fontSize: '14px',
    fontFamily: '"Arial", "Helvetica", sans-serif',
    textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
    animation: 'gradientShift 4s ease-in-out infinite, textGlow 2s ease-in-out infinite alternate',
    position: 'relative',
    display: 'inline-block',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
};

const shineEffectStyle = {
    position: 'absolute',
    top: 0,
    left: '-200%',
    width: '200%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 25%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0.2) 75%, transparent 100%)',
    animation: 'shine 3s ease-in-out infinite',
    pointerEvents: 'none',
    borderRadius: '25px'
};

const arrowStyle = {
    position: 'absolute',
    right: '-8px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 0,
    height: 0,
    borderLeft: `8px solid ${colors.white}`,
    borderTop: '8px solid transparent',
    borderBottom: '8px solid transparent',
    filter: 'drop-shadow(2px 0 2px rgba(0,0,0,0.1))'
};

const chatbotImageStyle = (isMobile) => ({
    width: isMobile ? '75px' : '85px',
    height: isMobile ? '75px' : '85px',
    borderRadius: '50%',
    background: `linear-gradient(135deg,rgb(208, 243, 255) 0%, #f0f8ff 30%,rgb(157, 211, 255) 70%, #ffeaa7 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 6px 15px rgba(38, 34, 98, 0.47)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer'
});

const imageStyle = {
    width: '120%',
    height: '120%',
    objectFit: 'cover',
    borderRadius: '50%',
    transition: 'all 0.3s ease'
};

const pulseRingStyle = {
    position: 'absolute',
    top: '-2px',
    left: '-2px',
    right: '-2px',
    bottom: '-2px',
    border: `2px solid ${colors.primary}`,
    borderRadius: '50%',
    animation: 'pulse 3s infinite'
};

export default Chatbot;